import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useParentKidLogin } from '@/auth/useAuth';
import { openKidPageInNewTab } from '@/auth/openKidPage';
import { api } from '@/lib/api';
import { usePortalLocale } from '@/lib/i18n/PortalLocaleContext';
import { PORTAL_NUMBER_LOCALE } from '@/lib/i18n/portalLocale';
import type { MyClassesResponse } from './myClasses';
import { TeachingTeam } from './teachers/TeachingTeam';

export function ParentClassDetails({ classId, familyId }: { classId: string; familyId: string }) {
  const { t, locale } = usePortalLocale();
  const childLogin = useParentKidLogin();
  const navigate = useNavigate();
  const [opening, setOpening] = useState<string | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const openChild = async (kidId: string) => {
    setOpening(kidId);
    setOpenError(null);
    try {
      await openKidPageInNewTab(
        childLogin,
        kidId,
        navigate,
        `/learn/classroom/${encodeURIComponent(classId)}`,
      );
    } catch {
      setOpenError(
        t('Could not open your child’s classroom. Try again, or use their login settings below.'),
      );
    } finally {
      setOpening(null);
    }
  };
  const booking = useQuery({
    queryKey: ['families', familyId, 'my-classes'],
    queryFn: () =>
      api<MyClassesResponse>(`/families/${familyId}/my-classes`, { principal: 'user' }),
  });
  const rows =
    booking.data?.enrollments.filter(
      (row) => row.class.id === classId && ['active', 'completed'].includes(row.status),
    ) ?? [];
  const cls = rows[0]?.class;
  const sessions = cls?.sessions?.length
    ? [...cls.sessions]
    : cls
      ? [{ starts_at: cls.starts_at, ends_at: cls.ends_at, venue: cls.venue, lesson: null }]
      : [];
  sessions.sort(
    (left, right) => new Date(left.starts_at).getTime() - new Date(right.starts_at).getTime(),
  );
  const next = sessions.find((session) => new Date(session.ends_at).getTime() >= Date.now());
  const when = (value: string) =>
    new Date(value).toLocaleString(PORTAL_NUMBER_LOCALE[locale], {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short',
    });
  const where = (venue: NonNullable<typeof cls>['venue']) =>
    venue
      ? `${venue.name}, ${venue.address_line}, ${venue.suburb} ${venue.state} ${venue.postcode}`
      : t('Location to be confirmed — check your booking.');
  return (
    <section className="rounded-2xl bg-wash-sky p-5" aria-label={t('Your family class')}>
      <h2 className="text-lg font-bold text-ink">{t('Your family class')}</h2>
      {booking.isLoading && <p role="status">{t('Loading your class schedule…')}</p>}
      {booking.isError && (
        <div role="alert">
          <p>{t('Your schedule could not load. Your lesson content is still available below.')}</p>
          <button className="btn-pill-secondary mt-3" onClick={() => void booking.refetch()}>
            {t('Retry schedule')}
          </button>
        </div>
      )}
      {cls && (
        <>
          <p className="mt-2 font-semibold">
            {t('Course access: you + {names}', {
              names: rows.map((row) => row.kid.nickname).join(', '),
            })}
          </p>
          <p className="mt-2">
            {t('Teacher:')} <TeachingTeam team={cls.teaching_team ?? []} />
          </p>
          <p className="mt-2 font-semibold">
            {next
              ? t('Next class: {time}', { time: when(next.starts_at) })
              : t('Scheduled classes have finished. You can revisit your own work.')}
          </p>
          <p className="mt-2 text-sm">{where(next?.venue ?? cls.venue)}</p>
          <details className="mt-3">
            <summary className="cursor-pointer font-semibold">{t('Full class schedule')}</summary>
            <ol className="mt-3 space-y-3">
              {sessions.map((session, index) => (
                <li key={`${session.starts_at}-${index}`}>
                  {session.lesson?.title && <strong>{session.lesson.title} · </strong>}
                  {when(session.starts_at)} — {where(session.venue ?? cls.venue)}
                </li>
              ))}
            </ol>
          </details>
          <p className="mt-4 text-sm">
            {t(
              'Bring your device and charger. Sign in to your parent account for your own work. Your child uses their own login.',
            )}
          </p>
          <div className="mt-3 space-y-3">
            {rows.map((row) => (
              <div key={row.kid.id} className="flex flex-wrap gap-3">
                <button
                  className="btn-pill-secondary"
                  disabled={opening !== null}
                  onClick={() => void openChild(row.kid.id)}
                >
                  {opening === row.kid.id
                    ? t('Opening…')
                    : t('Open {name}’s classroom', { name: row.kid.nickname })}
                </button>
                <Link className="btn-pill-ghost" to={`/portal/family/${row.kid.id}/settings`}>
                  {t('Prepare {name}’s login', { name: row.kid.nickname })}
                </Link>
              </div>
            ))}
          </div>
          {openError && (
            <p role="alert" className="mt-3 text-ink-soft">
              {openError}
            </p>
          )}
        </>
      )}
      {booking.isSuccess && !cls && (
        <p className="mt-2">
          {t('Check your booking for the latest class details.')}{' '}
          <Link to="/portal/courses#upcoming" className="underline">
            {t('Your bookings')}
          </Link>
        </p>
      )}
    </section>
  );
}
